import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import {
    ModalOverlay,
    ModalPanel,
    ModalHeader,
    ModalTitle,
    ModalCloseButton,
    ModalBody,
    ModalActions,
    ModalButton,
    ModalInput,
    ModalLabel,
} from './shared/Modal';
import type { DocumentOverviewItem } from '../types';

export interface MergeDocumentsModalProps {
    visible: boolean;
    /** All currently-selected docs. The combined document keeps the doc-id
     *  of the case-order-first one (decided by the parent), but the
     *  modal only asks the user for the *title*. */
    selected: DocumentOverviewItem[];
    /** Called with the user-edited title for the combined document. */
    onConfirm: (title: string) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
}

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SectionLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const HelperText = styled.div`
    font-size: 12px;
    color: #888;
`;

const OrderList = styled.ol`
    list-style: none;
    counter-reset: merge-order;
    margin: 0;
    padding: 0;
    border: 1px solid #ececec;
    border-radius: 6px;
    max-height: 240px;
    overflow-y: auto;
`;

const OrderRow = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px 8px 0;
    font-size: 13px;
    color: #333;
    counter-increment: merge-order;

    & + & {
        border-top: 1px solid #f0f0f0;
    }

    &::before {
        content: counter(merge-order);
        flex-shrink: 0;
        width: 28px;
        text-align: center;
        font-variant-numeric: tabular-nums;
        color: #888;
        font-size: 12px;
        font-weight: 600;
    }
`;

const RowTitle = styled.span`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RowPages = styled.span`
    color: #888;
    font-size: 12px;
    flex-shrink: 0;
    padding-right: 12px;
`;

const Totals = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #555;
`;

const ReversibilityNote = styled.div`
    padding: 10px 12px;
    background: #f6f6f6;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    font-size: 12px;
    color: #555;
    line-height: 1.5;
`;

export const MergeDocumentsModal: React.FC<MergeDocumentsModalProps> = ({
    visible,
    selected,
    onConfirm,
    onCancel,
    isSubmitting = false,
}) => {
    // Display (and merge) the docs in case order regardless of how the
    // table is sorted — this is the order in which their pages will end up
    // inside the combined document.
    const orderedSelected = React.useMemo(
        () => [...selected].sort((a, b) => (a.firstPageIndex ?? 0) - (b.firstPageIndex ?? 0)),
        [selected],
    );

    // The title input is local to the modal: it prefills from the
    // case-order-first doc but the user can type anything. Reset on each
    // open so a previous typed value doesn't bleed into a different
    // selection.
    const defaultTitle = orderedSelected[0]?.title ?? '';
    const [title, setTitle] = React.useState(defaultTitle);
    React.useEffect(() => {
        if (visible) setTitle(defaultTitle);
    }, [visible, defaultTitle]);

    const totalPages = orderedSelected.reduce((acc, d) => acc + (d.pageCount ?? 0), 0);
    const trimmedTitle = title.trim();
    const canSubmit = !isSubmitting && trimmedTitle.length > 0 && orderedSelected.length >= 2;

    if (!visible) return null;

    return (
        <ModalOverlay $visible={visible} onClick={onCancel}>
            <ModalPanel
                $width="560px"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="merge-docs-title"
            >
                <ModalHeader>
                    <ModalTitle id="merge-docs-title">
                        Merge {orderedSelected.length} documents into one
                    </ModalTitle>
                    <ModalCloseButton type="button" onClick={onCancel} aria-label="Cancel merge">
                        <X size={16} />
                    </ModalCloseButton>
                </ModalHeader>

                <ModalBody>
                    <FieldGroup>
                        <ModalLabel htmlFor="merge-title-input">Title</ModalLabel>
                        <ModalInput
                            id="merge-title-input"
                            type="text"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            disabled={isSubmitting}
                            placeholder="Title for the combined document"
                            autoFocus
                            // Quick keyboard escape — Esc cancels, Cmd/Ctrl-Enter confirms.
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') onCancel();
                                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSubmit) {
                                    onConfirm(trimmedTitle);
                                }
                            }}
                        />
                        <HelperText>Prefilled from the first selected document; edit freely.</HelperText>
                    </FieldGroup>

                    <FieldGroup>
                        <SectionLabel>Order</SectionLabel>
                        <HelperText>
                            The combined document will contain pages from these documents, in this order:
                        </HelperText>
                        <OrderList>
                            {orderedSelected.map(doc => (
                                <OrderRow key={doc.docId}>
                                    <RowTitle title={doc.title}>{doc.title}</RowTitle>
                                    <RowPages>{doc.pageCount ?? 0} pages</RowPages>
                                </OrderRow>
                            ))}
                        </OrderList>
                        <Totals>
                            <span>{orderedSelected.length} documents</span>
                            <span>{totalPages} pages total</span>
                        </Totals>
                    </FieldGroup>

                    <ReversibilityNote>
                        After merging, the boundaries between these documents will no longer be marked.
                        Splitting them back apart later would require manually re-creating the boundaries.
                    </ReversibilityNote>
                </ModalBody>

                <ModalActions>
                    <ModalButton type="button" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </ModalButton>
                    <ModalButton
                        type="button"
                        $variant="primary"
                        onClick={() => onConfirm(trimmedTitle)}
                        disabled={!canSubmit}
                        title={!trimmedTitle ? 'Enter a title' : undefined}
                    >
                        {isSubmitting ? 'Merging…' : 'Merge into one document'}
                    </ModalButton>
                </ModalActions>
            </ModalPanel>
        </ModalOverlay>
    );
};

export default MergeDocumentsModal;
